<template>
    <Layout>
        <PageHeader :title="$t('module.articleCategory')" :items="breadcrumbs" />
        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                @keyup.enter="applySearch" />
                            <b-input-group-append><b-button variant="info" @click="applySearch">
                                    <i class="mdi mdi-magnify"></i>
                                </b-button></b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="col-lg-8 d-flex justify-content-lg-end">
                        <b-button variant="info" @click="openForm()">
                            <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.articleCategory')]) }}
                        </b-button>
                    </div>
                </div>
                <div v-if="!status.list" class="d-flex justify-content-center p-5"><b-spinner variant="info" /></div>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive no-local-sorting
                        @sort-changed="changeSort">
                        <template #cell(actions)="{ item }">
                            <a href="javascript:void(0);" class="text-info" v-b-tooltip.hover
                                :title="$t('label.updateX', [$t('module.articleCategory')])"
                                @click.stop="openForm(item)">
                                <i class="mdi mdi-pencil-outline font-size-18"></i>
                            </a>
                            <a href="javascript:void(0);" class="text-danger ml-2" v-b-tooltip.hover
                                :title="$t('label.deleteX', [$t('module.articleCategory')])"
                                @click.stop="confirmDelete(item)">
                                <i class="mdi mdi-delete-outline font-size-18"></i>
                            </a>
                        </template>
                    </b-table>
                    <div v-if="pagination.total"
                        class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
                        <small class="text-muted mb-2 mb-md-0">{{ $tc('label.results', pagination.total,
                            [pagination.total]) }}</small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="article-category-form"
            :title="$t(category.uuid ? 'label.updateX' : 'label.createX', [$t('module.articleCategory')])" size="lg"
            hide-footer hide-header-close centered @hidden="closeForm">
            <b-container fluid>
                <ArticleCategoryForm v-if="formVisible" :category-u-u-i-d="category.uuid"
                    :search-criteria="searchCriteria" @done="closeForm" />
            </b-container>
        </b-modal>
    </Layout>
</template>

<script>
import { mapState } from 'vuex';
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import i18n from '@/i18n';
import { articleCategoryMethods } from '@/state/api';
import { ARTICLE_CATEGORY_MODULE } from '@/state/api/articleCategory.state';
import ArticleCategoryForm from '@/views/pages/admin/articleCategory/form';

const defaultCriteria = () => ({
    pageNumber: 1,
    pageSize: 15,
    searchArray: [],
    sortBy: 'created_at',
    orderBy: 'desc',
});

export default {
    name: 'ArticleCategoryListing',
    page: { title: i18n.t('module.articleCategory') },
    components: { Layout, PageHeader, ArticleCategoryForm },
    data() {
        return {
            searchText: '',
            currentPage: 1,
            formVisible: false,
            category: { uuid: '' },
            searchCriteria: defaultCriteria(),
            breadcrumbs: [
                { text: i18n.t('module.communication'), href: '/communications' },
                { text: i18n.t('module.articleCategory'), active: true },
            ],
            fields: [
                { key: 'name', label: i18n.t('label.name'), sortable: true },
                { key: 'createdAt', label: i18n.t('label.createdAt'), sortable: true, class: 'text-nowrap' },
                { key: 'updatedAt', label: i18n.t('label.updatedAt'), sortable: true, class: 'text-nowrap' },
                { key: 'actions', label: i18n.t('label.action'), sortable: false, class: 'text-nowrap' },
            ],
        };
    },
    computed: {
        ...mapState(ARTICLE_CATEGORY_MODULE, ['status']),
        tableRows() {
            return this.$store.state.api.articleCategory.list.data || [];
        },
        pagination() {
            return this.$store.state.api.articleCategory.list.pagination || {};
        },
    },
    created() {
        this.loadCategories();
    },
    methods: {
        ...articleCategoryMethods,
        loadCategories() {
            return this.getAllArticleCategory({ filter: this.searchCriteria });
        },
        applySearch() {
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : []
            };
            this.loadCategories();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadCategories();
        },
        changeSort({ sortBy, sortDesc }) {
            const apiFields = { name: 'name', createdAt: 'created_at', updatedAt: 'updated_at' };
            if (!apiFields[sortBy]) return;
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                sortBy: apiFields[sortBy],
                orderBy: sortDesc ? 'desc' : 'asc'
            };
            this.loadCategories();
        },
        openForm(category = null) {
            this.category = { uuid: category?.uuid || '' };
            this.formVisible = true;
            this.$nextTick(() => this.$bvModal.show('article-category-form'));
        },
        closeForm() {
            this.$bvModal.hide('article-category-form');
            this.formVisible = false;
            this.category = { uuid: '' };
        },
        async confirmDelete(category) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                this.$t('label.confirmDeleteX', [category.name]),
                {
                    title: this.$t('label.confirmDeleteArticleCategory'),
                    okTitle: this.$t('label.delete'), okVariant: 'danger',
                    cancelTitle: this.$t('label.cancel'),
                    cancelVariant: 'outline-secondary', centered: true
                },
            );
            if (confirmed) await this.deleteArticleCategory({ uuid: category.uuid, filter: this.searchCriteria });
        },
    },
};
</script>
